import React, { useState } from "react";
import Box from "@mui/material/Box";
import isEqual from "lodash/isEqual";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CircularProgress from "@mui/material/CircularProgress";
import useMutation from "../../../../hooks/useMutation";
import {
  DELETE_INVENTORY_ITEM_MUTATION,
  ADD_INVENTORY_ITEM_MUTATION,
} from "../../../../constants/graphql/mutations";
import { INVENTORY_REQUEST_QUERY } from "../../../../constants/graphql/queries";
import { getInventoryFields } from "../CartItemFields";

export default function EditPickLocationsModal({
  asset,
  item,
  rackLocations = [],
  inventoryRequest,
  onClose,
}) {
  const [locations, setLocations] = useState([...rackLocations]);
  const [loading, setLoading] = useState(false);
  const [deleteInventoryItem] = useMutation(DELETE_INVENTORY_ITEM_MUTATION);
  const [addInventoryItem] = useMutation(ADD_INVENTORY_ITEM_MUTATION, {
    refetchQueries: [{ query: INVENTORY_REQUEST_QUERY, variables: { id: inventoryRequest.id } }],
  });
  const originalTotalQuantity = item.quantity;
  const currentTotalQuantity = locations.reduce((acc, location) => acc + location.sub_qty, 0);
  const hasQuantityError = currentTotalQuantity !== originalTotalQuantity;
  const hasEmptyFields = locations.some(
    location =>
      location.sub_qty <= 0 ||
      !location.warehouse_locations ||
      location.warehouse_locations.length === 0
  );

  const handleQuantityChange = (index, value) => {
    const newLocations = locations.map((location, i) =>
      i === index ? { ...location, sub_qty: parseInt(value) || 0 } : location
    );
    setLocations(newLocations);
  };

  const handleRackLocationChange = (index, newValue) => {
    const newLocations = locations.map((location, i) =>
      i === index ? { ...location, warehouse_locations: newValue || [] } : location
    );
    setLocations(newLocations);
  };

  const handleAddNewLocation = () => {
    const { entry_uid, ...baseLocation } = rackLocations[0] || {};
    const newLocation = {
      ...baseLocation,
      warehouse_locations: [],
      sub_qty: 0,
    };
    setLocations([...locations, newLocation]);
  };

  const handleRemoveLocation = index => {
    const newLocations = locations.filter((_, i) => i !== index);
    setLocations(newLocations);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await deleteInventoryItem({
        variables: { id: item.id },
      });

      const fields = getInventoryFields(item);
      const { data } = await addInventoryItem({
        variables: {
          ...fields,
          inventoryRequestId: inventoryRequest.id,
          rackLocationChanges: locations,
        },
      });
      const newItem = data?.addInventoryItem?.inventoryItem;
      onClose(newItem);
    } catch (error) {
      console.error("Error updating inventory item:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography align="center" fontWeight={500} fontSize={22} color="text.primary">
          Edit Pick Location(s)
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box pt={2}>
          {locations.map((location, index) => {
            const subQty = location.sub_qty || 0;
            const assetLocation = asset.rackLocations.find(rl =>
              isEqual(rl.warehouse_locations, location.warehouse_locations)
            );

            return (
              <Grid container spacing={2} key={index} sx={{ mb: 2, alignItems: "center" }}>
                <Grid size={5}>
                  <Autocomplete
                    fullWidth
                    value={location.warehouse_locations}
                    onChange={(event, newValue) => handleRackLocationChange(index, newValue)}
                    options={asset.rackLocations.map(rl => rl.warehouse_locations)}
                    getOptionDisabled={option =>
                      locations.some(
                        (loc, i) => i !== index && isEqual(loc.warehouse_locations, option)
                      )
                    }
                    getOptionLabel={option =>
                      Array.isArray(option) ? option.map(loc => loc.location_name).join(", ") : ""
                    }
                    isOptionEqualToValue={(option, value) => isEqual(option, value)}
                    renderInput={params => (
                      <TextField
                        {...params}
                        label="Storage Location"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    type="number"
                    value={subQty}
                    onChange={e => handleQuantityChange(index, e.target.value)}
                    size="small"
                    inputProps={{ min: 0, max: location.sub_qty }}
                  />
                </Grid>
                <Grid size={3}>
                  <TextField
                    fullWidth
                    label="Available to Pick"
                    value={
                      assetLocation ? assetLocation?.sub_qty - assetLocation?.allocated_quantity : 0
                    }
                    size="small"
                    disabled
                  />
                </Grid>
                <Grid size={1}>
                  <IconButton
                    onClick={() => handleRemoveLocation(index)}
                    size="small"
                    disabled={locations.length <= 1}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            );
          })}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddNewLocation}
              size="small"
            >
              Add Location
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {hasQuantityError && (
          <Typography color="error" variant="body2" sx={{ mr: 2 }}>
            Total must equal {originalTotalQuantity}
          </Typography>
        )}
        <Button
          disabled={hasQuantityError || hasEmptyFields || loading}
          color="primary"
          variant="contained"
          onClick={handleSave}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
